/**
 * The chat, and the stand-in agent behind it.
 *
 * No model is connected. What runs here is a scripted turn over the SAME
 * fixture store the meetings list reads, which is the point: the citations it
 * produces resolve to real rows, so clicking `[1]` opens the meeting the
 * sentence came from. A chat that cites invented sources would look identical
 * and prove nothing about the surface.
 *
 * The turn is emitted as events rather than returned, because the shape of the
 * real thing is an SSE stream and the timeline has to be able to render a
 * half-finished answer. `run-state` folding, resume and cancellation belong to
 * the wiring phase; what is modelled now is the part the UI has to get right —
 * work appears before the answer, and the answer streams in.
 */
import { apiGet, apiSend, apiStream } from '@/lib/api'
import { findMeeting } from './meetings'
import { spacesSync } from './spaces'
import { nextId } from './store'
import type { Meeting } from './types'

/* ---------- Timeline ---------- */

/** One turn behind a citation, as it read when the answer was written. */
export interface CitationTurn {
  turnId: number
  speaker: string
  text: string
  startSeconds: number | null
  /** Shown around the cited words to make them readable; not itself cited. */
  context?: boolean
}

/**
 * A cited source. Carries the words it points at, not just a label.
 *
 * The service sends these with the text rather than leaving the client to fetch
 * them: it has the passage in hand at the moment it resolves the reference, so
 * shipping it costs bytes and no queries — and a preview that has to fetch
 * before it can show anything is a preview nobody waits for.
 */
export interface AnswerCitation {
  /** `[[12.137]]`, exactly as it appears in the answer. */
  ref: string
  kind: 'meeting' | 'chunk' | 'turn'
  meetingId: string
  meetingNumber: number
  title: string
  startsAt: string
  chunkIndex?: number
  headline?: string
  speakers: string[]
  /** Seconds into the recording, which is also seconds into the video. */
  startSeconds: number | null
  endSeconds: number | null
  /** Empty for a whole-meeting citation, which points at no particular words. */
  turns: CitationTurn[]
}

export interface MessageItem {
  kind: 'message'
  id: string
  role: 'user' | 'assistant'
  text: string
  /** Reasoning summary. Folded into the steps row, never rendered as answer. */
  thinking: string
  citations: AnswerCitation[]
  streaming: boolean
}

export interface ToolItem {
  kind: 'tool'
  id: string
  tool: 'search_meetings' | 'read_notes' | 'list_spaces'
  title: string
  summary: string | null
  argsPreview: Record<string, unknown>
  /** Null while running. */
  outcome: 'ok' | 'error' | null
}

export type TimelineItem = MessageItem | ToolItem

export interface Thread {
  /**
   * The id the service knows this thread by, once it has one.
   *
   * A thread exists in this tab the moment someone starts typing, and on the
   * server only once a question has been asked — so the local id is what the
   * URL and the sidebar are keyed on, and this is what requests carry. Adopting
   * the server's id as the route would mean the address bar changing under a
   * reader mid-sentence.
   */
  serverId: string | null
  id: string
  title: string
  createdAt: string
  /**
   * What this conversation is scoped to, or null for every space.
   *
   * It lives on the thread rather than app-wide because it is a property of the
   * question being asked: "what did we decide in Northwind" is a different
   * conversation from the same words asked across every deal, and coming back
   * to one of them later should not depend on what some global control happens
   * to be set to.
   */
  spaceIds: string[]
  /**
   * Specific meetings to read instead of searching. Empty means "search
   * everything `spaceIds` covers".
   *
   * A list rather than one id because "these two calls" is a real question —
   * the buyer meeting and the one where the answer changed — and a single-value
   * scope could only ask it by widening to the whole space and hoping the
   * search picked the right two.
   */
  meetingIds: string[]
  items: TimelineItem[]
}

/**
 * What a conversation is asked against.
 *
 * Two shapes, and only two: spaces (none meaning all of them), or a set of
 * meetings. They are stored in one value with one writer so they cannot
 * contradict — `normalizeScope` is the only thing that decides which half wins.
 *
 * Spaces are a LIST because the picker's space control is the meetings list's
 * own multi-select one, and "the two live deals" is a question that control can
 * ask and a single-value scope could not.
 */
export interface ChatScope {
  spaceIds: string[]
  meetingIds: string[]
}

/** How many meetings one turn will read. See `MAX_CONTEXT_MEETINGS` in the picker. */
export const MAX_CONTEXT_MEETINGS = 20

export const ALL_MEETINGS: ChatScope = { spaceIds: [], meetingIds: [] }

/**
 * Picked meetings win, and they decide the spaces.
 *
 * The space half of the scope is a FILTER in the picker, so it survives being
 * pointed somewhere else while meetings stay ticked; storing that as-is is how
 * a scope ends up claiming one space AND a meeting from another. Once meetings
 * are chosen, the spaces are simply the ones those meetings are filed in.
 *
 * The selection is also capped here rather than only in the picker: the cap is
 * a property of what a turn can read, so a caller that has not heard of it must
 * not be able to route around it.
 */
function normalizeScope(scope: ChatScope): ChatScope {
  const meetings = scope.meetingIds
    .map((id) => findMeeting(id))
    .filter((meeting): meeting is Meeting => meeting !== undefined)
    .slice(0, MAX_CONTEXT_MEETINGS)

  if (meetings.length === 0) return { spaceIds: [...scope.spaceIds], meetingIds: [] }

  return {
    spaceIds: [
      ...new Set(
        meetings.flatMap((meeting) => (meeting.spaceId === null ? [] : [meeting.spaceId]))
      ),
    ],
    meetingIds: meetings.map((meeting) => meeting.id),
  }
}

/* ---------- Thread store ---------- */

const THREADS = new Map<string, Thread>()

export function createThread(scope: ChatScope = ALL_MEETINGS): Thread {
  const normalized = normalizeScope(scope)
  const thread: Thread = {
    id: nextId('thread'),
    serverId: null,
    title: 'New chat',
    createdAt: new Date().toISOString(),
    spaceIds: normalized.spaceIds,
    meetingIds: normalized.meetingIds,
    items: [],
  }
  THREADS.set(thread.id, thread)
  publish(thread.id)
  return thread
}

/**
 * Re-scopes a conversation.
 *
 * Both halves are written together, always, through `normalizeScope` — setting
 * them independently is what let the composer show a scope spanning everything
 * and one call at the same time.
 *
 * Answers already in the thread keep the sources they were given — the scope
 * decides what the NEXT question searches, and rewriting history to match would
 * make citations disagree with the text above them.
 */
export function setThreadScope(threadId: string, scope: ChatScope): void {
  const thread = THREADS.get(threadId)
  if (!thread) return
  const normalized = normalizeScope(scope)
  thread.spaceIds = normalized.spaceIds
  thread.meetingIds = normalized.meetingIds
  publish(threadId)
}

export function getThread(id: string): Thread | undefined {
  return THREADS.get(id)
}

/**
 * Most recent first — the sidebar list is a stack, not an archive.
 *
 * Cached because `useSyncExternalStore` compares snapshots by identity: a fresh
 * array on every call is a new snapshot on every render, which React reads as
 * "the store changed again" and loops until it bails out.
 */
let threadList: Thread[] = []

export function listThreads(): Thread[] {
  return threadList
}

/**
 * Everything this account has asked, newest first.
 *
 * Threads started in this tab but never sent have no server id and are kept —
 * a half-typed conversation disappearing when the sidebar refreshes would be
 * its own bug.
 */
export async function loadThreads(): Promise<void> {
  const { threads } = await apiGet<{ threads: WireThread[] }>('/api/chat/threads')
  const unsent = [...THREADS.values()].filter((thread) => thread.serverId === null)
  THREADS.clear()
  for (const wire of threads) {
    THREADS.set(wire.id, {
      id: wire.id,
      serverId: wire.id,
      title: wire.title || 'New conversation',
      createdAt: wire.created_at,
      spaceIds: [],
      meetingIds: wire.scope_meeting_id ? [wire.scope_meeting_id] : [],
      items: [],
    })
  }
  for (const thread of unsent) THREADS.set(thread.id, thread)
  publish(null)
}

/** Fills in a thread's messages, which the list does not carry. */
export async function openThread(id: string): Promise<void> {
  const thread = THREADS.get(id)
  if (!thread?.serverId || thread.items.length > 0) return
  const body = await apiGet<{ messages: WireMessage[] }>(
    `/api/chat/threads/${encodeURIComponent(thread.serverId)}`
  )
  thread.items = body.messages.flatMap((message) => hydrate(message))
  publish(id)
}

/**
 * One stored message, as the timeline draws it.
 *
 * The tool rows are rebuilt from what was recorded rather than replayed, so a
 * reopened conversation shows the same steps without re-running anything.
 */
function hydrate(message: WireMessage): TimelineItem[] {
  const steps: TimelineItem[] = (message.steps ?? []).map((step) => ({
    kind: 'tool' as const,
    id: nextId('tool'),
    tool: 'search_meetings' as const,
    title: step.label,
    summary: step.found === 0 ? 'nothing found' : `${step.found} result${step.found === 1 ? '' : 's'}`,
    argsPreview: {},
    outcome: 'ok' as const,
  }))

  return [
    ...steps,
    {
      kind: 'message',
      id: nextId('msg'),
      role: message.role,
      text: message.content,
      thinking: '',
      citations: (message.citations ?? []).map(toCitation),
      streaming: false,
    },
  ]
}

interface WireThread {
  id: string
  title: string
  scope_meeting_id: string | null
  created_at: string
}

/**
 * A citation as it comes off the wire, or out of a thread stored months ago.
 *
 * Every field past the first four is optional because threads written before
 * citations addressed anything smaller than a meeting are still in the table,
 * and they are supposed to still open. Those resolve to a whole-meeting
 * citation with no words attached, which is exactly what they were.
 */
interface WireCitation {
  ref?: string
  kind?: 'meeting' | 'chunk' | 'turn'
  meetingId: string
  meetingNumber?: number
  title: string
  startedAt: string | null
  chunkIndex?: number
  headline?: string
  speakers?: string[]
  startSeconds?: number | null
  endSeconds?: number | null
  turns?: CitationTurn[]
}

function toCitation(wire: WireCitation): AnswerCitation {
  return {
    ref: wire.ref ?? '',
    kind: wire.kind ?? 'meeting',
    meetingId: wire.meetingId,
    meetingNumber: wire.meetingNumber ?? 0,
    title: wire.title,
    startsAt: wire.startedAt ?? '',
    ...(wire.chunkIndex !== undefined ? { chunkIndex: wire.chunkIndex } : {}),
    ...(wire.headline ? { headline: wire.headline } : {}),
    speakers: wire.speakers ?? [],
    startSeconds: wire.startSeconds ?? null,
    endSeconds: wire.endSeconds ?? null,
    turns: wire.turns ?? [],
  }
}

interface WireMessage {
  role: 'user' | 'assistant'
  content: string
  citations?: WireCitation[]
  steps?: Array<{ label: string; found: number }>
}

export function deleteThread(id: string): void {
  RUNS.get(id)?.abort()
  const thread = THREADS.get(id)
  THREADS.delete(id)
  SNAPSHOTS.delete(id)
  publish(id)

  if (thread?.serverId) {
    // The row has already left the sidebar. A failed delete surfaces on the
    // next load rather than by reappearing under the reader.
    void apiSend('DELETE', `/api/chat/threads/${encodeURIComponent(thread.serverId)}`).catch(
      () => undefined
    )
  }
}

/* ---------- Subscriptions ---------- */

/**
 * A run belongs to the THREAD, not to the view.
 *
 * Asking the first question of a new chat navigates `/chat` → `/chat/:id`, and
 * those are two different route components — so React unmounts the page and
 * mounts another one mid-answer. With the run owned by the component, its
 * cleanup aborted the turn one step in and the answer never arrived; the reader
 * saw a question, a "Worked · 1 step" header, and nothing else.
 *
 * Keeping the run here also matches where it is going: the real endpoint
 * streams server-side, and a remount must not cancel it.
 */
export interface ThreadSnapshot {
  items: TimelineItem[]
  running: boolean
  spaceIds: string[]
  meetingIds: string[]
}

/** Stable identity for "no such thread", which `useSyncExternalStore` requires. */
const EMPTY: ThreadSnapshot = { items: [], running: false, spaceIds: [], meetingIds: [] }

const SNAPSHOTS = new Map<string, ThreadSnapshot>()
const RUNS = new Map<string, AbortController>()
const LISTENERS = new Set<() => void>()

function publish(threadId: string | null): void {
  const thread = threadId === null ? undefined : THREADS.get(threadId)
  if (thread && threadId !== null) {
    SNAPSHOTS.set(threadId, {
      items: thread.items,
      running: RUNS.has(threadId),
      spaceIds: thread.spaceIds,
      meetingIds: thread.meetingIds,
    })
  }
  threadList = [...THREADS.values()].sort((left, right) =>
    right.createdAt.localeCompare(left.createdAt)
  )
  for (const listener of LISTENERS) listener()
}

/** One listener set for every thread: the sidebar needs the list, not a thread. */
export function subscribeChat(listener: () => void): () => void {
  LISTENERS.add(listener)
  return () => LISTENERS.delete(listener)
}

export function threadSnapshot(threadId: string | null): ThreadSnapshot {
  if (!threadId) return EMPTY
  return SNAPSHOTS.get(threadId) ?? EMPTY
}

/**
 * Starts a turn and returns immediately. Errors are swallowed on purpose:
 * an aborted run is a normal outcome, and there is no failure path to report
 * until something can actually fail.
 */
export function startTurn(threadId: string, prompt: string): void {
  RUNS.get(threadId)?.abort()
  const controller = new AbortController()
  RUNS.set(threadId, controller)
  publish(threadId)

  // The scope comes off the thread, not off the caller: the pickers write it
  // there, so a turn cannot run against a scope the composer is not showing.
  const thread = THREADS.get(threadId)

  void runTurn(
    threadId,
    prompt,
    thread?.spaceIds ?? [],
    thread?.meetingIds ?? [],
    () => publish(threadId),
    controller.signal
  ).finally(() => {
    if (RUNS.get(threadId) === controller) RUNS.delete(threadId)
    publish(threadId)
  })
}

export function stopTurn(threadId: string): void {
  RUNS.get(threadId)?.abort()
}



/**
 * The first message names the thread.
 *
 * Kept whole up to a generous cap, because the sidebar clips it to the width it
 * actually has. Cutting at a fixed word count here as well produced a column
 * where every row ended in an ellipsis well short of the edge — two truncations
 * stacked, and the visible one measured nothing.
 */
function titleFrom(prompt: string): string {
  const text = prompt.trim().replace(/\s+/g, ' ')
  return text.length <= 80 ? text : text.slice(0, 80).replace(/\s\S*$/, '') + '…'
}

/* ---------- The scripted turn ---------- */

export type RunEvent =
  | { type: 'items'; items: TimelineItem[] }
  | { type: 'done' }






/**
 * Runs one turn.
 *
 * Mutates the thread in place and calls `emit` after every change, so the view
 * re-renders off one source rather than keeping its own copy of the timeline.
 * Private: callers go through `startTurn`, which owns the abort controller and
 * the subscription, because a run outlives the component that asked for it.
 */
/**
 * One turn, against `POST /api/chat`.
 *
 * The server streams: each tool is announced before it runs and amended when it
 * finishes, and the answer arrives as deltas. That shape is why the timeline
 * can show work in progress at all — a single response at the end would leave
 * the folded steps row empty for the whole run.
 *
 * The conversation is sent with every request. The endpoint is stateless by
 * design, so the thread lives here, in this tab, and the server is told only as
 * much of it as the next answer needs.
 */
async function runTurn(
  threadId: string,
  prompt: string,
  _spaceIds: string[],
  meetingIds: string[],
  emit: (event: RunEvent) => void,
  signal: AbortSignal
): Promise<void> {
  const thread = THREADS.get(threadId)
  if (!thread) throw new Error(`Unknown thread ${threadId}`)

  if (thread.items.length === 0) thread.title = titleFrom(prompt)

  const push = (item: TimelineItem) => {
    thread.items = [...thread.items, item]
    emit({ type: 'items', items: thread.items })
  }
  const replace = (item: TimelineItem) => {
    thread.items = thread.items.map((current) => (current.id === item.id ? item : current))
    emit({ type: 'items', items: thread.items })
  }

  push({
    kind: 'message',
    id: nextId('msg'),
    role: 'user',
    text: prompt,
    thinking: '',
    citations: [],
    streaming: false,
  })

  let answer: MessageItem = {
    kind: 'message',
    id: nextId('msg'),
    role: 'assistant',
    text: '',
    thinking: '',
    citations: [],
    streaming: true,
  }
  push(answer)

  const tools = new Map<string, ToolItem>()

  try {
    const response = await chatStream(
      {
        message: prompt,
        // Absent on the first turn: the service creates the thread and names it
        // back over the stream.
        thread_id: thread.serverId,
        // One chosen meeting narrows every search to it. More than one has no
        // server-side equivalent, so the scope stays advisory there.
        meeting_id: meetingIds.length === 1 ? meetingIds[0] : null,
      },
      signal
    )

    for await (const event of response) {
      if (event.type === 'thread') {
        thread.serverId = event.id
        if (thread.items.length <= 2) thread.title = event.title || thread.title
        continue
      }

      if (event.type === 'tool') {
        const item: ToolItem = {
          kind: 'tool',
          id: nextId('tool'),
          tool: event.name === 'read_meeting_notes' ? 'read_notes' : 'search_meetings',
          title: event.label,
          summary: null,
          argsPreview: {},
          outcome: null,
        }
        tools.set(event.label, item)
        push(item)
        continue
      }

      if (event.type === 'tool_done') {
        const item = tools.get(event.label)
        if (item) {
          replace({
            ...item,
            outcome: 'ok',
            summary:
              event.found === 0
                ? 'nothing found'
                : `${event.found} result${event.found === 1 ? '' : 's'}`,
          })
        }
        continue
      }

      if (event.type === 'delta') {
        answer = { ...answer, text: answer.text + event.text }
        replace(answer)
        continue
      }

      if (event.type === 'citations') {
        // Cumulative, and sent the moment a reference first appears in the text
        // rather than once at the end — so the chip that just rendered already
        // has its source. Replacing rather than merging keeps the client from
        // holding a citation the service has stopped vouching for.
        answer = { ...answer, citations: event.items.map(toCitation) }
        replace(answer)
        continue
      }

      if (event.type === 'error') {
        answer = { ...answer, text: answer.text || event.message }
        replace(answer)
      }
    }
  } catch (error) {
    // An abort is the reader stopping the turn, which is not a failure — the
    // partial answer stays on screen exactly as it was.
    if (!signal.aborted) {
      answer = {
        ...answer,
        text: answer.text || (error as Error).message || 'The assistant is unavailable right now.',
      }
      replace(answer)
    }
  } finally {
    // Any tool still marked running was cut off with the stream.
    for (const item of tools.values()) {
      const current = thread.items.find((entry) => entry.id === item.id)
      if (current && current.kind === 'tool' && current.outcome === null) {
        replace({ ...current, outcome: 'error', summary: 'stopped' })
      }
    }
    replace({ ...answer, streaming: false })
  }
}

/** The turn's events, decoded from the server's SSE stream. */
async function* chatStream(
  body: Record<string, unknown>,
  signal: AbortSignal
): AsyncGenerator<ServerEvent> {
  const response = await apiStream('/api/chat', body, signal)
  const reader = response.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  for (;;) {
    const { done, value } = await reader.read()
    buffer += decoder.decode(value, { stream: !done })

    let boundary = buffer.indexOf('\n\n')
    while (boundary !== -1) {
      const block = buffer.slice(0, boundary)
      buffer = buffer.slice(boundary + 2)
      const data = block
        .split('\n')
        .filter((line) => line.startsWith('data:'))
        .map((line) => line.slice(5).trimStart())
        .join('\n')
      if (data) {
        try {
          yield JSON.parse(data) as ServerEvent
        } catch {
          // A truncated event is not worth failing the turn over.
        }
      }
      boundary = buffer.indexOf('\n\n')
    }

    if (done) break
  }
}

type ServerEvent =
  | { type: 'thread'; id: string; title: string }
  | { type: 'tool'; name: string; label: string }
  | { type: 'tool_done'; name: string; label: string; found: number }
  | { type: 'delta'; text: string }
  | { type: 'citations'; items: WireCitation[] }
  | { type: 'error'; message: string }
  | { type: 'done' }


export function suggestions(): string[] {
  const space = spacesSync()[0]
  return [
    'What did we decide about the working capital normalisation?',
    'Summarise the open questions across the last two weeks',
    space ? `What are the risks flagged in ${space.name}?` : 'What risks were flagged recently?',
    'Which action items are still open, and who owns them?',
  ]
}
